import React, {useState, useMemo} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {BaseCRUDPage, NavigationContext} from '../common';
import {Input, RequiredIndicator, Column, SearchableSelect, Modal, Button, CurrencySelect} from '../../components';
import {voyageService, portService, shippingLineService} from '../../services';
import {Voyage, VoyageCreate, VoyageUpdate} from '../../types';
import {useReferenceData} from '../../hooks';
import {formatDate} from '../../utils/tableUtils';

interface VoyagesPageNavState {
    searchQuery?: string;
    searchColumn?: string;
    selectedRowId?: string;
    loadedCount?: number;
}

interface VoyageFormData {
    voyage_code: string;
    vessel_description: string;
    departure: string;
    currency: string;
    remarks: string;
    port_id: string;
    shipping_line_id: string;
}

interface VoyagesPageProps {
    title?: string;
    readonly?: boolean;
    isArchived?: boolean;
    isBooking?: boolean;
}

const VoyagesPage: React.FC<VoyagesPageProps> = ({ title = "Voyage Management", readonly = false, isArchived = false, isBooking }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = (location.state as VoyagesPageNavState | null) ?? null;
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [archiveDate, setArchiveDate] = useState('');
    const [archiving, setArchiving] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    
    // Create a wrapped service that adds is_archived and is_booking filters
    const filteredVoyageService = useMemo(() => ({
        getAll: (params: any) => voyageService.getAll({
            ...params,
            is_archived: isArchived,
            ...(isBooking !== undefined && { is_booking: isBooking })
        }),
        search: (query: string, column: string, params: any) =>
            voyageService.search(query, column, {
                ...params,
                is_archived: isArchived,
                ...(isBooking !== undefined && { is_booking: isBooking })
            }),
        create: voyageService.create.bind(voyageService),
        update: voyageService.update.bind(voyageService),
        delete: voyageService.delete.bind(voyageService),
        bulkDelete: voyageService.bulkDelete.bind(voyageService),
        bulkArchive: isArchived ? undefined : voyageService.bulkArchive.bind(voyageService),
        getFilterOptions: voyageService.getFilterOptions.bind(voyageService),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [isArchived, isBooking, refreshKey]);

    // Extra params for filter options (is_archived, is_booking)
    const filterExtraParams = useMemo(() => ({
        is_archived: isArchived,
        ...(isBooking !== undefined && { is_booking: isBooking })
    }), [isArchived, isBooking]);

    const handleArchiveByDate = async () => {
        if (!archiveDate) return;
        setArchiving(true);
        try {
            const result = await voyageService.archiveByDate(archiveDate);
            alert(`${result.archived_count} voyages archived successfully`);
            setArchiveModalOpen(false);
            setArchiveDate('');
            setRefreshKey(k => k + 1);
        } catch (error) {
            console.error('Error archiving voyages:', error);
            alert('Failed to archive voyages');
        } finally {
            setArchiving(false);
        }
    };
    
    const [formData, setFormData] = useState<VoyageFormData>({
        voyage_code: '',
        vessel_description: '',
        departure: '',
        currency: 'EUR',
        remarks: '',
        port_id: '',
        shipping_line_id: ''
    });

    // Reference data for dropdowns
    const {data: ports} = useReferenceData({
        loadFn: () => portService.getAll({limit: 1000}),
        enabled: true
    });

    const {data: shippingLines} = useReferenceData({
        loadFn: () => shippingLineService.getAll({limit: 1000}),
        enabled: true
    });


    // Define table columns
    const columns: Column[] = [
        {key: 'voyage_code', label: 'Voyage', sortable: true, searchable: true, filterable: true},
        {key: 'vessel_description', label: 'Vessel', sortable: true, searchable: true, filterable: true},
        {key: 'shipping_line_name', label: 'Owner', sortable: true, searchable: true, filterable: true},
        {key: 'remarks', label: 'Remarks', sortable: false, searchable: true},
        {
            key: 'departure',
            label: 'Departure',
            sortable: true,
            searchable: false,
            filterable: true,
            render: (value: string) => formatDate(value)
        },
        {key: 'port_name', label: 'POL', sortable: true, searchable: true, filterable: true},
        {key: 'total_number', label: 'Total Number', sortable: true, searchable: false},
        {key: 'total_loaded', label: 'Total Loaded', sortable: true, searchable: false}
    ];


    // Form fields component with error support
    const formFields = (fieldErrors: Record<string, string>, editingEntity?: Voyage) => (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Voyage Code <RequiredIndicator/>
                    </label>
                    <Input
                        type="text"
                        value={formData.voyage_code}
                        onChange={(e) => setFormData(prev => ({...prev, voyage_code: e.target.value}))}
                        required
                        placeholder="Enter voyage code"
                    />
                    {fieldErrors.voyage_code && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                      clipRule="evenodd"/>
                            </svg>
                            {fieldErrors.voyage_code}
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Vessel <RequiredIndicator/>
                    </label>
                    <Input
                        type="text"
                        value={formData.vessel_description}
                        onChange={(e) => setFormData(prev => ({...prev, vessel_description: e.target.value}))}
                        required
                        placeholder="Enter vessel description"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Owner (Shipping Line) <RequiredIndicator/>
                    </label>
                    <SearchableSelect
                        options={shippingLines.map(line => ({
                            value: line.id,
                            label: `${line.name} (${line.code})`
                        }))}
                        value={formData.shipping_line_id}
                        onChange={(value) => setFormData(prev => ({...prev, shipping_line_id: value}))}
                        placeholder="Search by name or code..."
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Port of Loading <RequiredIndicator/>
                    </label>
                    <SearchableSelect
                        options={ports.map(port => ({
                            value: port.id,
                            label: `${port.port} (${port.code}) - ${port.country}`
                        }))}
                        value={formData.port_id}
                        onChange={(value) => setFormData(prev => ({...prev, port_id: value}))}
                        placeholder="Search by port name or code..."
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Departure Date <RequiredIndicator/>
                    </label>
                    <Input
                        type="date"
                        value={formData.departure}
                        onChange={(e) => setFormData(prev => ({...prev, departure: e.target.value}))}
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Currency <RequiredIndicator/>
                    </label>
                    <CurrencySelect
                        value={formData.currency}
                        onChange={(value) => setFormData(prev => ({...prev, currency: value}))}
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Remarks
                </label>
                <textarea
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({...prev, remarks: e.target.value}))}
                    placeholder="Enter remarks (optional)"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
            </div>

            {editingEntity && (
                <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md space-y-2">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-300">Read-only Information</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                            <span className="text-gray-600 dark:text-gray-400">Total Number:</span>
                            <span className="ml-2 font-medium dark:text-gray-200">{editingEntity.total_number}</span>
                        </div>
                        <div>
                            <span className="text-gray-600 dark:text-gray-400">Total Loaded:</span>
                            <span className="ml-2 font-medium dark:text-gray-200">{editingEntity.total_loaded}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Modal icon
    const modalIcon = (
        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor"
                 viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
            </svg>
        </div>
    );

    const archiveButton = !isArchived && !readonly ? (
        <Button
            onClick={() => setArchiveModalOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-sm font-medium"
        >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            Archive
        </Button>
    ) : null;

    return (
        <>
            <BaseCRUDPage<Voyage, VoyageCreate, VoyageUpdate>
                title={title}
                entityName="voyage"
                columns={columns}
                service={filteredVoyageService}
                formFields={formFields}
                formData={formData}
                setFormData={setFormData}
                requiredFields={['voyage_code', 'vessel_description', 'departure', 'currency', 'port_id', 'shipping_line_id']}
                getInitialFormData={() => ({
                    voyage_code: '',
                    vessel_description: '',
                    departure: '',
                    currency: 'EUR',
                    remarks: '',
                    port_id: '',
                    shipping_line_id: ''
                })}
                toCreateData={(formData) => ({
                    voyage_code: formData.voyage_code,
                    vessel_description: formData.vessel_description,
                    departure: formData.departure,
                    currency: formData.currency,
                    remarks: formData.remarks || undefined,
                    port_id: formData.port_id,
                    shipping_line_id: formData.shipping_line_id,
                    is_booking: isBooking ?? false
                })}
                toUpdateData={(formData, original) => {
                    const updateData: VoyageUpdate = {};
                    if (formData.voyage_code !== original.voyage_code) updateData.voyage_code = formData.voyage_code;
                    if (formData.vessel_description !== original.vessel_description) updateData.vessel_description = formData.vessel_description;
                    // Compare dates properly
                    const formDate = new Date(formData.departure).toISOString().slice(0, 10);
                    const originalDate = new Date(original.departure).toISOString().slice(0, 10);
                    if (formDate !== originalDate) updateData.departure = formData.departure;
                    if (formData.currency !== original.currency) updateData.currency = formData.currency;
                    if (formData.remarks !== (original.remarks || '')) updateData.remarks = formData.remarks || null;
                    if (formData.port_id !== original.port_id) updateData.port_id = formData.port_id;
                    if (formData.shipping_line_id !== original.shipping_line_id) updateData.shipping_line_id = formData.shipping_line_id;
                    return updateData;
                }}
                toFormData={(voyage) => {
                    // Format departure date for date input (YYYY-MM-DD)
                    const departureDate = new Date(voyage.departure);
                    const formattedDeparture = departureDate.toISOString().slice(0, 10);

                    return {
                        voyage_code: voyage.voyage_code,
                        vessel_description: voyage.vessel_description,
                        departure: formattedDeparture,
                        currency: voyage.currency,
                        remarks: voyage.remarks || '',
                        port_id: voyage.port_id,
                        shipping_line_id: voyage.shipping_line_id
                    };
                }}
                modalIcon={modalIcon}
                modalTitle={(isEdit) => ({
                    title: isEdit ? 'Edit Voyage' : 'Create New Voyage',
                    subtitle: isEdit ? 'Update voyage information' : 'Add a new voyage to the system'
                })}
                customDeleteConfirm={(voyage) => `Are you sure you want to delete voyage "${voyage.voyage_code}"?`}
                defaultSearchColumn="voyage_code"
                initialSearchQuery={locationState?.searchQuery}
                initialSearchColumn={locationState?.searchColumn}
                initialSelectedRowId={locationState?.selectedRowId}
                initialLoadCount={locationState?.loadedCount}
                onRowDoubleClick={(voyage, context: NavigationContext) => navigate(`/voyage-details/${voyage.id}`, {
                    state: {
                        from: {
                            pathname: location.pathname,
                            state: {
                                searchQuery: context.searchQuery,
                                searchColumn: context.searchColumn,
                                selectedRowId: voyage.id,
                                loadedCount: context.loadedCount
                            }
                        }
                    }
                })}
                readonly={readonly}
                enableBulkArchive={!isArchived}
                additionalHeaderContent={archiveButton}
                enableServerSideFilters={true}
                extraFilterParams={filterExtraParams}
            />

            {/* Archive by Date Modal */}
            <Modal
                isOpen={archiveModalOpen}
                onClose={() => setArchiveModalOpen(false)}
                title={
                    <div className="flex items-center space-x-2">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                            <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Archive Voyages by Date</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Archive all voyages before a specific date</p>
                        </div>
                    </div>
                }
                size="sm"
                footer={
                    <div className="flex justify-end space-x-3">
                        <Button
                            onClick={() => setArchiveModalOpen(false)}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 px-6 py-2.5 font-medium"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleArchiveByDate}
                            disabled={!archiveDate || archiving}
                            className={`px-6 py-2.5 font-medium ${
                                archiveDate && !archiving
                                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {archiving ? 'Archiving...' : 'Archive'}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        All voyages with departure dates <strong>before</strong> the selected date will be archived.
                    </p>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Archive voyages before
                        </label>
                        <Input
                            type="date"
                            value={archiveDate}
                            onChange={(e) => setArchiveDate(e.target.value)}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default VoyagesPage;
